import { createContext, useContext, type ReactNode } from 'react'

/**
 * What the surface a control sits on has already decided about it.
 *
 * Both facts here belong to the surface rather than to each control — the row
 * a field is drawn in, the role the reader has — and threading either through
 * would mean every component between the two carrying a prop it does nothing
 * with. So the surface states it once and the parts read it, the way
 * `LightThemeScope` states the palette.
 */

const QuietFields = createContext(false)

/**
 * Declares that the controls inside are datasheet controls.
 *
 * A control that forgot to be told would render a box in a column of
 * borderless ones with nothing to catch it.
 */
export function QuietFieldScope({
  quiet = true,
  children,
}: {
  /** `false` gives the controls inside their boxes back, inside a quiet sheet. */
  quiet?: boolean
  children: ReactNode
}) {
  return <QuietFields.Provider value={quiet}>{children}</QuietFields.Provider>
}

/** `quiet` as a prop still wins, for a control outside a datasheet row. */
export function useQuietField(override?: boolean) {
  const inherited = useContext(QuietFields)
  return override ?? inherited
}

const ReadOnly = createContext(false)

/**
 * Declares that nothing inside may be changed.
 *
 * A field disables itself, and so does a button that is not marked as one that
 * changes nothing. It is an affordance, never the rule — the rule is the role
 * the server checks on the request the control would have sent.
 *
 * Nested, it only adds: `readOnly={false}` inside a read-only surface leaves it
 * read-only. A region that changes nothing — a filter bar, a search — says so
 * with `reads`, the way `Button` and `Menu` do, and stays live inside one.
 *
 * It reaches what reads it: `Button`, `Menu`, `DashedAddButton` and the six
 * controls below, plus the few components elsewhere that own a whole
 * collection and read `useDisabled` to stop offering to change it. A control
 * that folds neither in stays live inside a scope.
 */
export function ReadOnlyScope({
  readOnly = true,
  reads = false,
  children,
}: {
  readOnly?: boolean
  /** Lifts an outer scope for a region whose controls change nothing. */
  reads?: boolean
  children: ReactNode
}) {
  const inherited = useContext(ReadOnly)
  return (
    <ReadOnly.Provider value={!reads && (inherited || readOnly)}>
      {children}
    </ReadOnly.Provider>
  )
}

/**
 * Whether this control is disabled: because the caller said so, or because
 * the surface refuses changes.
 *
 * One reading of the rule, so a control added to the folder cannot be the one
 * that misses it.
 */
export function useDisabled(disabled?: boolean) {
  const readOnly = useContext(ReadOnly)
  return disabled || readOnly
}

/** The same fold for a control that spreads what its caller passed. */
export function useControlProps<T extends { disabled?: boolean }>(props: T): T {
  return { ...props, disabled: useDisabled(props.disabled) }
}
